import { useState } from 'react';
import { get, post } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { formatCents, formatRatePpm } from '../../shared/money';
import { formatDateTime, formatPhone } from '../../shared/format';
import { Button, Field, Sheet, Spinner, useToast } from '../ui/kit';
import { Barcode, usePrint } from '../ui/print';
import { useCloudPrint } from '../lib/printing';
import { usePlan } from '../lib/plan';
import { QrCode } from '../ui/qr';
import { saleReceiptTitle, type PayStamp } from '../../shared/receipts';
import { ReceiptMark } from '../ui/legal';

const METHOD: Record<string, string> = { cash: 'Cash', card: 'Card', store_credit: 'Store credit' };

export interface StoreInfo { name: string; address1: string | null; address2: string | null; city: string | null; state: string | null; zip: string | null; phone: string | null }

export interface ReceiptData {
  /** The store's policies, one per line, printed at the bottom. */
  policies?: string | null;
  /** Trade-ins taken for this sale: their credit came off the phone. */
  trade_ins?: { number: string; device: string; imei: string | null; serial: string | null; credit_cents: number }[];
  business_name: string;
  logo_url: string;
  website: string;
  footer: string;
  store: StoreInfo;
  receipt_no: number;
  created_at: number;
  employee: string;
  customer: string | null;
  lines: { kind?: string; description: string; detail: string | null; qty: number; unit_price_cents: number; discount_cents: number; points_discount_cents?: number; net_cents: number; refunded_qty: number }[];
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  tax_rate_ppm: number;
  /** Taxes removed from the sale ("Resale", "Shipped out of state"): why. */
  tax_removed_reason?: string | null;
  total_cents: number;
  payments: { method: string; amount_cents: number; tendered_cents: number | null; change_cents: number | null; card_last4: string | null; card_brand: string | null }[];
  /** parts: how each refund's money went back (several when it went back the way a split sale was paid). */
  refunds: { refund_no: number; total_cents: number; method: string; created_at: number; parts?: { method: string; amount_cents: number }[] }[];
  refunded_cents: number;
  points: { redeemed: number; discount_cents: number; earned: number; balance: number; member_code: string } | null;
}

/** "Tax (9.25%)", or "Tax removed (Resale)" when a manager took the tax off. */
export function taxLabel(r: { tax_rate_ppm: number; tax_removed_reason?: string | null }): string {
  return r.tax_removed_reason ? `Tax removed (${r.tax_removed_reason})` : `Tax (${formatRatePpm(r.tax_rate_ppm)})`;
}

export function storeLines(s: StoreInfo): string[] {
  const addr = [s.address1, s.address2].filter(Boolean).join(', ');
  const city = [s.city, s.state].filter(Boolean).join(', ') + (s.zip ? ` ${s.zip}` : '');
  return [addr, city.trim()].filter(Boolean);
}

/** Logo (falls back to the business name if the picture can't load) and the store's contact lines. */
/** The built-in West Coast Wireless logo already shows the phone number and address. */
const CONTACT_LOGO = /\/wcw-receipt-logo\.png$/;

export function ReceiptHeader({ logo, business, store, website }: { logo: string; business: string; store: StoreInfo; website?: string }) {
  const [logoOk, setLogoOk] = useState(true);
  const showLogo = !!logo && logoOk;
  // With the full logo, the name, phone and address aren't printed a second time under it.
  const full = showLogo && CONTACT_LOGO.test(logo);
  return (
    <div className="head">
      {showLogo ? <img className={full ? 'logo logo-full' : 'logo'} src={logo} alt={business} onError={() => setLogoOk(false)} /> : <div className="biz">{business}</div>}
      {full ? null : (
        <>
          <div className="strong">{business} · {store.name}</div>
          {storeLines(store).map((l) => <div key={l} className="dim">{l}</div>)}
          <div className="dim">{[store.phone ? formatPhone(store.phone) : null, website].filter(Boolean).join(' · ')}</div>
        </>
      )}
    </div>
  );
}

/**
 * PAID or NOT PAID, big, the way the thermal printer prints it: NOT PAID white on black (money
 * to take), the others in a box; the receipt that paid it, or what's due, under it.
 */
export function PayStampView({ pay }: { pay: PayStamp }) {
  return (
    <div className="pay-wrap" aria-label={`${pay.tag}: ${pay.detail}`}>
      <div className={`pay-stamp ${pay.state}`}>{pay.tag}</div>
      <div className="pay-detail">{pay.detail}</div>
    </div>
  );
}

/** The store's website, big and bold at the bottom of every receipt, ticket and invoice. */
export function SiteLine({ website }: { website?: string | null }) {
  const site = (website ?? '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  return site ? <div className="site-line"><span>Shop, sell and book repairs online</span><strong>{site}</strong></div> : null;
}

/** "DATA RESPONSIBILITY: Please back up…": the label before the colon prints bold. */
const POLICY_LABEL = /^([A-Z0-9][A-Z0-9 &/'’().-]{1,48}):\s*(.*)$/;
const isPolicyHeading = (l: string) => l.length <= 70 && !l.includes(':') && l === l.toUpperCase() && /[A-Z]/.test(l);

/**
 * The store's policies at the bottom of a receipt or ticket (Settings → Business & receipts), one
 * per line. A first line in capitals is the heading; "LABEL: text" prints the label bold; lines
 * starting with "* " or "- " are indented under the line above; short lines after the last
 * labeled one ("WEST COAST WIRELESS", a slogan) print centered as a sign-off.
 */
export function ReceiptPolicies({ title = 'STORE POLICIES', text }: { title?: string; text?: string | null }) {
  let lines = (text ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return null;
  let heading = title;
  if (lines.length > 1 && isPolicyHeading(lines[0])) {
    heading = lines[0];
    lines = lines.slice(1);
  }
  const bare = (l: string) => l.replace(/^[*•-]\s+/, '');
  let lastLabeled = -1;
  lines.forEach((l, i) => { if (POLICY_LABEL.test(bare(l))) lastLabeled = i; });
  let signOff: string[] = [];
  const tail = lastLabeled >= 0 ? lines.slice(lastLabeled + 1) : [];
  if (tail.length && tail.length <= 3 && tail.every((l) => l.length <= 60 && !/^[*•-]\s/.test(l))) {
    signOff = tail;
    lines = lines.slice(0, lastLabeled + 1);
  }
  return (
    <div className="policies">
      <hr />
      <div className="c strong">{heading}</div>
      <ul>
        {lines.map((l, i) => {
          const sub = /^[*•-]\s+/.test(l);
          const m = POLICY_LABEL.exec(bare(l));
          // "REPAIR WARRANTY:" with nothing after it leads into the lines under it.
          const cls = [sub ? 'sub' : '', m && !m[2] ? 'lead' : ''].filter(Boolean).join(' ');
          return <li key={i} className={cls || undefined}>{m ? <><strong>{m[1]}:</strong>{m[2] ? ` ${m[2]}` : ''}</> : bare(l)}</li>;
        })}
      </ul>
      {signOff.length ? <div className="policy-sign">{signOff.map((l, i) => <div key={i} className={i === 0 ? 'strong' : undefined}>{l}</div>)}</div> : null}
    </div>
  );
}

function change(r: ReceiptData) {
  return r.payments.reduce((s, p) => s + (p.method === 'cash' && p.change_cents ? p.change_cents : 0), 0);
}

/** Each refund, one line per way its money went back: "Refund #5001 (Card)", "Refund #5001 (Store credit)". */
export function refundLines(r: Pick<ReceiptData, 'refunds'>): { key: string; label: string; cents: number }[] {
  return r.refunds.flatMap((f) => (f.parts?.length ? f.parts : [{ method: f.method, amount_cents: f.total_cents }]).map((p, i) => ({
    key: `${f.refund_no}-${i}`, label: `Refund #${f.refund_no} (${METHOD[p.method] ?? p.method})`, cents: p.amount_cents,
  })));
}

/** Discount the cashier gave (points are shown on their own line). */
function ownDiscount(r: ReceiptData) {
  return r.discount_cents - (r.points?.discount_cents ?? 0);
}
const lineDiscount = (l: ReceiptData['lines'][number]) => l.discount_cents - (l.points_discount_cents ?? 0);
const pts = (n: number) => n.toLocaleString('en-US');

/** Points earned, the new balance and the member QR to scan next time, side by side to save paper. */
function RewardsBlock({ r }: { r: ReceiptData }) {
  if (!r.points) return null;
  const p = r.points;
  return (
    <div className="rewards">
      <hr />
      <div className="rw">
        <QrCode text={p.member_code} size={72} label={`Member code ${p.member_code}`} />
        <div className="rw-t">
          <div className="strong">REWARDS</div>
          {p.redeemed ? <div className="rl"><span>Points used</span><span>−{pts(p.redeemed)}</span></div> : null}
          <div className="rl"><span>Points earned</span><span>+{pts(p.earned)}</span></div>
          <div className="rl strong"><span>Points balance</span><span>{pts(p.balance)}</span></div>
          <div className="dim">{p.member_code}: show it at the counter to earn and use points.</div>
        </div>
      </div>
    </div>
  );
}

/** The customer's receipt for the 80 mm thermal printer. */
export function ReceiptPaper({ r }: { r: ReceiptData }) {
  const ch = change(r);
  return (
    <div className="receipt" aria-label={`Receipt ${r.receipt_no}`}>
      <ReceiptHeader logo={r.logo_url} business={r.business_name} store={r.store} website={r.website} />
      <div className="c strong">{saleReceiptTitle(r.lines)}</div>
      <div className="ticket-no">#{r.receipt_no}</div>
      <div className="bar"><Barcode value={String(r.receipt_no)} /></div>
      <dl className="meta">
        <dt>Date</dt><dd>{formatDateTime(r.created_at)}</dd>
        <dt>Served by</dt><dd>{r.employee}</dd>
        {r.customer ? <><dt>Customer</dt><dd>{r.customer}</dd></> : null}
      </dl>
      <hr />
      <table className="rtable">
        <thead><tr><th className="q">QTY</th><th>ITEM</th><th className="r">TOTAL</th></tr></thead>
        <tbody>
          {r.lines.map((l, i) => (
            <tr key={i}>
              <td className="q">{l.qty}</td>
              <td>
                {l.description}
                {l.detail ? <div className="dim">{l.detail}</div> : null}
                {l.qty > 1 ? <div className="dim">{formatCents(l.unit_price_cents)} each</div> : null}
                {lineDiscount(l) ? <div className="dim">Discount −{formatCents(lineDiscount(l))}</div> : null}
                {l.refunded_qty ? <div className="dim">Refunded: {l.refunded_qty}</div> : null}
              </td>
              <td className="r">{formatCents(l.qty * l.unit_price_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <hr />
      <div className="rl"><span>Subtotal</span><span>{formatCents(r.subtotal_cents)}</span></div>
      {ownDiscount(r) ? <div className="rl"><span>Discount</span><span>−{formatCents(ownDiscount(r))}</span></div> : null}
      {r.points?.redeemed ? <div className="rl"><span>Points ({pts(r.points.redeemed)})</span><span>−{formatCents(r.points.discount_cents)}</span></div> : null}
      <div className="rl"><span>{taxLabel(r)}</span><span>{formatCents(r.tax_cents)}</span></div>
      <div className="rl tot"><span>TOTAL</span><span>{formatCents(r.total_cents)}</span></div>
      {r.trade_ins?.length ? (
        <>
          <hr />
          <div className="kind-banner sm">TRADE-IN</div>
          {r.trade_ins.map((t) => (
            <div key={t.number}>
              <div className="rl"><span className="strong">{t.number} · {t.device}</span><span>{formatCents(t.credit_cents)}</span></div>
              {t.imei || t.serial ? <div className="dim">{t.imei ? `IMEI ${t.imei}` : `Serial ${t.serial}`}</div> : null}
            </div>
          ))}
          <div className="dim">The trade-in credit paid part of this sale.</div>
        </>
      ) : null}
      <hr />
      {r.payments.map((p, i) => (
        <div key={i}>
          <div className="rl"><span>{p.method === 'store_credit' && r.trade_ins?.length ? 'Trade-in credit' : METHOD[p.method] ?? p.method}{p.card_last4 ? ` •••• ${p.card_last4}` : ''}</span><span>{formatCents(p.amount_cents)}</span></div>
          {p.method === 'cash' && p.tendered_cents && p.tendered_cents !== p.amount_cents ? <div className="rl dim"><span>Cash given</span><span>{formatCents(p.tendered_cents)}</span></div> : null}
        </div>
      ))}
      {ch ? <div className="rl strong"><span>Change</span><span>{formatCents(ch)}</span></div> : null}
      {refundLines(r).map((f) => (
        <div key={f.key} className="rl"><span>{f.label}</span><span>−{formatCents(f.cents)}</span></div>
      ))}
      <RewardsBlock r={r} />
      <hr />
      {r.footer ? <div className="c strong pickup-note">{r.footer}</div> : null}
      <div className="c dim">Keep this receipt for returns and warranty.</div>
      <ReceiptPolicies text={r.policies} />
      <SiteLine website={r.website} />
      <ReceiptMark />
    </div>
  );
}

/** The same sale as a full-page invoice (8.5 x 11). */
export function InvoicePaper({ r }: { r: ReceiptData }) {
  const ch = change(r);
  return (
    <div className="invoice">
      <div className="ih">
        <div>
          {r.logo_url ? <img src={r.logo_url} alt={r.business_name} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} /> : null}
          <div className="biz">{r.business_name}</div>
          <div>{r.store.name} store</div>
          {storeLines(r.store).map((l) => <div key={l}>{l}</div>)}
          <div>{[r.store.phone ? formatPhone(r.store.phone) : null, r.website].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="right">
          <h1>INVOICE</h1>
          <div className="strong">Receipt #{r.receipt_no}</div>
          <div>{formatDateTime(r.created_at)}</div>
          <div className="barc"><Barcode value={String(r.receipt_no)} height={40} /></div>
        </div>
      </div>
      <div className="two">
        <div><div className="lab">Customer</div><div className="strong">{r.customer ?? 'Walk-in customer'}</div></div>
        <div><div className="lab">Served by</div><div>{r.employee}</div></div>
      </div>
      <table>
        <thead><tr><th style={{ width: 32 }}>#</th><th>Item</th><th className="r">Qty</th><th className="r">Price</th><th className="r">Total</th></tr></thead>
        <tbody>
          {r.lines.map((l, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td><div className="strong">{l.description}</div>{l.detail ? <div style={{ color: '#555' }}>{l.detail}</div> : null}{lineDiscount(l) ? <div style={{ color: '#555' }}>Discount −{formatCents(lineDiscount(l))}</div> : null}</td>
              <td className="r">{l.qty}</td>
              <td className="r">{formatCents(l.unit_price_cents)}</td>
              <td className="r">{formatCents(l.qty * l.unit_price_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="totals">
        <div><span>Subtotal</span><span>{formatCents(r.subtotal_cents)}</span></div>
        {ownDiscount(r) ? <div><span>Discount</span><span>−{formatCents(ownDiscount(r))}</span></div> : null}
        {r.points?.redeemed ? <div><span>Points ({pts(r.points.redeemed)})</span><span>−{formatCents(r.points.discount_cents)}</span></div> : null}
        <div><span>{taxLabel(r)}</span><span>{formatCents(r.tax_cents)}</span></div>
        <div className="grand"><span>Total</span><span>{formatCents(r.total_cents)}</span></div>
        {r.payments.map((p, i) => <div key={i}><span>{METHOD[p.method] ?? p.method}{p.card_last4 ? ` •••• ${p.card_last4}` : ''}</span><span>{formatCents(p.amount_cents)}</span></div>)}
        {ch ? <div><span>Change</span><span>{formatCents(ch)}</span></div> : null}
        {refundLines(r).map((f) => <div key={f.key}><span>{f.label}</span><span>−{formatCents(f.cents)}</span></div>)}
      </div>
      {r.points ? (
        <div className="inv-rewards">
          <QrCode text={r.points.member_code} size={84} label={`Member code ${r.points.member_code}`} />
          <div>
            <div className="strong">Rewards · member {r.points.member_code}</div>
            <div>{r.points.redeemed ? `Points used ${pts(r.points.redeemed)} · ` : ''}Points earned {pts(r.points.earned)} · Balance {pts(r.points.balance)}</div>
            <div style={{ color: '#555' }}>Show this code at the counter to earn and use points.</div>
          </div>
        </div>
      ) : null}
      {r.footer ? <div className="terms">{r.footer}</div> : null}
      <div className="sigs"><div>Customer signature</div><div>Date</div></div>
      <SiteLine website={r.website} />
      <div className="foot">Thank you for your business</div>
      <ReceiptMark />
    </div>
  );
}

export function ReceiptActions({ saleId, r, customerPhone, customerEmail }: { saleId: string; r: ReceiptData; customerPhone?: string | null; customerEmail?: string | null }) {
  const toast = useToast();
  const print = usePrint();
  const cloud = useCloudPrint();
  const job = cloud.jobFor('sale_receipt', saleId);
  // Texting runs on West Coast Wireless's own number: its store only, for now. Email is every shop's.
  const texting = usePlan().home;
  const [send, setSend] = useState<null | 'sms' | 'email'>(null);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const open = (ch: 'sms' | 'email') => {
    setTo(ch === 'sms' ? formatPhone(customerPhone ?? '') : customerEmail ?? '');
    setSend(ch);
  };
  const doSend = async () => {
    if (!send) return;
    setBusy(true);
    try {
      const res = await post(`/api/sales/${saleId}/send`, { channel: send, to });
      // An email that didn't go out stays open, so the address can be fixed and sent again.
      if (res.status === 'failed') { toast(res.message, 'error'); return; }
      toast(res.message, res.status === 'not_configured' || res.status === 'blocked' ? 'info' : 'good');
      setSend(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not send.', 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="row-wrap">
        <Button icon="printer" busy={job?.state === 'sending'} onClick={() => cloud.printOut({ kind: 'sale_receipt', refId: saleId, title: `Receipt #${r.receipt_no}`, browser: () => print(<ReceiptPaper r={r} />, 'thermal') })}>Print receipt</Button>
        <Button icon="printer" onClick={() => print(<InvoicePaper r={r} />, 'letter')}>Print full page</Button>
        {texting ? <Button icon="chat" onClick={() => open('sms')}>Text</Button> : null}
        <Button icon="mail" onClick={() => open('email')}>Email</Button>
      </div>
      {send ? (
        <Sheet
          title={send === 'sms' ? 'Text the receipt' : 'Email the receipt'}
          onClose={() => setSend(null)}
          footer={<><Button onClick={() => setSend(null)}>Cancel</Button><Button variant="primary" busy={busy} onClick={doSend} disabled={!to.trim()}>Send</Button></>}
        >
          <Field label={send === 'sms' ? 'Mobile number' : 'Email address'}>
            <input className="input input-lg" autoFocus inputMode={send === 'sms' ? 'tel' : 'email'} value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <p className="small muted">Receipt #{r.receipt_no} for {formatCents(r.total_cents)}.</p>
        </Sheet>
      ) : null}
    </>
  );
}

export function useReceipt(saleId: string) {
  return useLoad(() => get<ReceiptData>(`/api/sales/${saleId}/receipt`), [saleId]);
}

export function ReceiptLoading() {
  return <Spinner label="Loading receipt" />;
}
